/**
 * useManufacturingPricing — ViewModel for /manufacturing/pricing.
 *
 * Two-level UI surface:
 *   1. List of raw materials (paginated; one row per active material
 *      with supplier-count + price range).
 *   2. Drill-down per material (fetched on demand) — every active
 *      supplier's pricing for that material. CRUD lives at this level.
 *
 * Per SOP 02 §"MVVM Pattern" + §"List Pagination". usePaginatedList
 * powers level 1; level 2 is a one-shot fetch + optimistic local
 * mutations.
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type {
  ManufacturingPricingDetailDto,
  ManufacturingPricingSummaryDto,
  SupplierPricingDto
} from '../models/manufacturing-pricing.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

// Single-source-of-truth: src/models/manufacturing-pricing.model.ts.
export type {
  ManufacturingPricingDetailDto,
  ManufacturingPricingSummaryDto,
  SupplierPricingDto
} from '../models/manufacturing-pricing.model'

export interface AddPricingInput {
  readonly supplier_id: string
  readonly seller_price_paisa: number
  readonly discounted_price_paisa: number | null
}

export interface UpdatePricingInput {
  readonly seller_price_paisa?: number
  readonly discounted_price_paisa?: number | null
}

export interface UseManufacturingPricingReturn {
  readonly items: ManufacturingPricingSummaryDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  /** Fetch one material's full supplier-pricing detail. */
  readonly fetchDetail: (
    rawMaterialId: string
  ) => Promise<{ ok: true; data: ManufacturingPricingDetailDto } | { ok: false; message: string }>
  readonly addPricing: (
    rawMaterialId: string,
    input: AddPricingInput
  ) => Promise<{ ok: true; data: SupplierPricingDto } | { ok: false; field: string | null; message: string }>
  readonly updatePricing: (
    rawMaterialId: string,
    pricingId: string,
    patch: UpdatePricingInput
  ) => Promise<{ ok: true; data: SupplierPricingDto } | { ok: false; field: string | null; message: string }>
  readonly deletePricing: (
    rawMaterialId: string,
    pricingId: string
  ) => Promise<{ ok: true } | { ok: false; message: string }>
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: ManufacturingPricingSummaryDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
  }
}

interface DetailBody {
  readonly success: true
  readonly data: ManufacturingPricingDetailDto
}

export function useManufacturingPricing(): UseManufacturingPricingReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<ManufacturingPricingSummaryDto, undefined>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.MANUFACTURINGPRICING}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return { items: body.data.items, total: body.data.total, extras: undefined }
      },
      [authedFetch]
    )
  })

  const fetchDetail = useCallback(
    async (rawMaterialId: string): Promise<
      { ok: true; data: ManufacturingPricingDetailDto } | { ok: false; message: string }
    > => {
      const url = fillPath(APIROUTES.MANUFACTURINGPRICINGSUPPLIERS, {
        id: rawMaterialId
      })
      const res = await authedFetch(url)
      if (!res.ok) {
        const { message } = await parseApiError(res)
        return { ok: false, message }
      }
      const body = (await res.json()) as DetailBody
      return { ok: true, data: body.data }
    },
    [authedFetch]
  )

  /**
   * Bump the list's local row for the affected material — supplier_count
   * + min/max are derived, so after a CUD on a supplier price we just
   * reload to keep the summary numbers honest. Cheaper than synthesizing
   * the new summary locally and risking drift.
   */
  const bumpSummary = useCallback(() => {
    paged.reload()
  }, [paged])

  const addPricing = useCallback(
    async (rawMaterialId: string, input: AddPricingInput) => {
      const r = await mutateJson<SupplierPricingDto>(
        authedFetch,
        fillPath(APIROUTES.MANUFACTURINGPRICINGSUPPLIERS, { id: rawMaterialId }),
        'POST',
        input
      )
      if (!r.ok) return r
      bumpSummary()
      return { ok: true as const, data: r.data }
    },
    [authedFetch, bumpSummary]
  )

  const updatePricing = useCallback(
    async (rawMaterialId: string, pricingId: string, patch: UpdatePricingInput) => {
      const r = await mutateJson<SupplierPricingDto>(
        authedFetch,
        fillPath(APIROUTES.MANUFACTURINGPRICINGSUPPLIERITEM, {
          id: rawMaterialId,
          pricingId
        }),
        'PATCH',
        patch
      )
      if (!r.ok) return r
      bumpSummary()
      return { ok: true as const, data: r.data }
    },
    [authedFetch, bumpSummary]
  )

  const deletePricing = useCallback(
    async (rawMaterialId: string, pricingId: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.MANUFACTURINGPRICINGSUPPLIERITEM, {
          id: rawMaterialId,
          pricingId
        }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      bumpSummary()
      return { ok: true as const }
    },
    [authedFetch, bumpSummary]
  )

  return {
    items: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    fetchDetail,
    addPricing,
    updatePricing,
    deletePricing
  }
}
